/** Parent app shell: parent-focused nav, language toggle, disclaimer. */
import { NavLink, useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';

interface PatientResponse {
  patient: { name: string; nickname?: string | null } | null;
}

const NAV: { to: string; label: { fr: string; en: string } }[] = [
  { to: '/', label: { fr: 'Aujourd’hui', en: 'Today' } },
  { to: '/journal', label: { fr: 'Journal', en: 'Journal' } },
  { to: '/trends', label: { fr: 'Tendances', en: 'Trends' } },
  { to: '/plan', label: { fr: 'Traitement', en: 'Plan' } },
  { to: '/schedule', label: { fr: 'Calendrier', en: 'Schedule' } },
  { to: '/more', label: { fr: 'Plus', en: 'More' } },
];

export function ParentLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['patient'], queryFn: () => api.get<PatientResponse>('/patient'), enabled: !!user });

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg">ToxoFollow</span>
          {data?.patient && <span className="text-brand-light text-sm">{data.patient.nickname || data.patient.name}</span>}
        </div>
        <div className="flex items-center gap-3 text-sm">
          <button onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')} className="px-2 py-1 rounded bg-brand border border-brand-light/30">
            {locale === 'fr' ? 'EN' : 'FR'}
          </button>
          <button onClick={() => { void logout().then(() => navigate('/login')); }} className="underline">
            {locale === 'fr' ? 'Quitter' : 'Logout'}
          </button>
        </div>
      </header>

      <main className="flex-1 p-4 max-w-2xl w-full mx-auto pb-24">{children}</main>

      {/* Bottom tab bar (mobile-web friendly) */}
      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 flex justify-around">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/'}
            className={({ isActive }) =>
              `flex-1 text-center py-3 text-xs ${isActive ? 'text-brand-dark font-semibold' : 'text-slate-500'}`
            }
          >
            {n.label[locale]}
          </NavLink>
        ))}
      </nav>

      <footer className="bg-slate-100 text-slate-500 text-[11px] px-4 py-2 border-t border-slate-200 mb-14">
        {t('disclaimer.short')}
      </footer>
    </div>
  );
}
